import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { db, auth } from "./firebase";
import {
  collection, addDoc, doc, getDoc, getDocs,
  onSnapshot, query, setDoc, updateDoc, where,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { Avatar, Box, IconButton, Typography } from "@mui/material";
import CallIcon from "@mui/icons-material/Call";
import CallEndIcon from "@mui/icons-material/CallEnd";
import VideocamIcon from "@mui/icons-material/Videocam";
import VideocamOffIcon from "@mui/icons-material/VideocamOff";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";

const ICE_SERVERS = {
  iceServers: [
    { urls: "stun:stun.l.google.com:19302" },
    { urls: "stun:stun1.l.google.com:19302" },
  ],
};

const formatDuration = (s) => {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
};

const CallContext = createContext(null);
export const useCall = () => useContext(CallContext);

export function CallProvider({ children }) {
  const [authUserId, setAuthUserId]             = useState(null);
  const [authUserName, setAuthUserName]         = useState("");
  const [authUserImage, setAuthUserImage]       = useState("");
  const [callState, setCallState]               = useState(null); // null | "calling" | "incoming" | "active"
  const [callType, setCallType]                 = useState(null); // "video" | "audio"
  const [incomingCallData, setIncomingCallData] = useState(null);
  const [otherUserInfo, setOtherUserInfo]       = useState({ displayName: "Usuario", photoURL: "" });
  const [micMuted, setMicMuted]                 = useState(false);
  const [camOff, setCamOff]                     = useState(false);
  const [callDuration, setCallDuration]         = useState(0);
  const peerConnectionRef = useRef(null);
  const localStreamRef    = useRef(null);
  const remoteStreamRef   = useRef(null);
  const callDocIdRef      = useRef(null);
  const callUnsubsRef     = useRef([]);
  const localVideoRef     = useRef(null);
  const remoteVideoRef    = useRef(null);
  const remoteAudioRef    = useRef(null);
  const callTimerRef      = useRef(null);
  const audioCtxRef       = useRef(null);
  const ringIntervalRef   = useRef(null);

  const stopRingtone = () => {
    clearInterval(ringIntervalRef.current);
    ringIntervalRef.current = null;
    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
    }
  };

  const startRingtone = () => {
    stopRingtone();
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = ctx;
      const beep = () => {
        if (!audioCtxRef.current) return;
        [[480, 0], [440, 0.25]].forEach(([freq, delay]) => {
          const osc  = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.type = 'sine';
          osc.frequency.value = freq;
          const t = ctx.currentTime + delay;
          gain.gain.setValueAtTime(0.35, t);
          gain.gain.exponentialRampToValueAtTime(0.001, t + 0.22);
          osc.start(t);
          osc.stop(t + 0.23);
        });
      };
      beep();
      ringIntervalRef.current = setInterval(beep, 2200);
    } catch (_) {}
  };

  // ── resolve current user info ─────────────────────────────────────────
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setAuthUserId(user.uid);
        try {
          const snap = await getDoc(doc(db, "users", user.uid));
          if (snap.exists()) {
            setAuthUserName(snap.data().displayName || "");
            setAuthUserImage(snap.data().profileImageUrl || "");
          }
        } catch (_) {}
      } else {
        setAuthUserId(null);
        setAuthUserName("");
        setAuthUserImage("");
      }
    });
    return () => unsub();
  }, []);

  // ── global incoming call listener (works on any screen) ──────────────
  useEffect(() => {
    if (!authUserId) return;
    const q = query(
      collection(db, "calls"),
      where("calleeId", "==", authUserId),
      where("status", "==", "ringing")
    );
    const unsub = onSnapshot(q, (snap) => {
      snap.docChanges().forEach(async (change) => {
        if (change.type === "added" && !callDocIdRef.current) {
          const data = change.doc.data();
          let callerPhoto = "";
          try {
            const callerSnap = await getDoc(doc(db, "users", data.callerId));
            if (callerSnap.exists())
              callerPhoto = callerSnap.data().profileImageUrl || callerSnap.data().photoURL || "";
          } catch (_) {}
          setOtherUserInfo({ displayName: data.callerName || "Usuario", photoURL: callerPhoto });
          setIncomingCallData({ callId: change.doc.id, callerId: data.callerId, type: data.type });
          setCallType(data.type);
          setCallState("incoming");
        }
      });
    });
    return () => unsub();
  }, [authUserId]);

  // ── ringtone while incoming ─────────────────────────────────────────
  useEffect(() => {
    if (callState === "incoming") {
      startRingtone();
    } else {
      stopRingtone();
    }
    return stopRingtone;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callState]);

  // ── reassign streams when overlay mounts (avoids ref-null race) ────────
  useEffect(() => {
    if (callState !== "active") return;
    const frame = requestAnimationFrame(() => {
      if (localVideoRef.current && localStreamRef.current)
        localVideoRef.current.srcObject = localStreamRef.current;
      if (remoteVideoRef.current && remoteStreamRef.current)
        remoteVideoRef.current.srcObject = remoteStreamRef.current;
      if (remoteAudioRef.current && remoteStreamRef.current)
        remoteAudioRef.current.srcObject = remoteStreamRef.current;
    });
    return () => cancelAnimationFrame(frame);
  }, [callState]);

  // ── call duration timer ───────────────────────────────────────────────
  useEffect(() => {
    if (callState === "active") {
      callTimerRef.current = setInterval(() => setCallDuration((d) => d + 1), 1000);
    } else {
      clearInterval(callTimerRef.current);
      setCallDuration(0);
    }
    return () => clearInterval(callTimerRef.current);
  }, [callState]);

  // ── endCall ───────────────────────────────────────────────────────────
  const endCall = () => {
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
    }
    if (remoteStreamRef.current) {
      remoteStreamRef.current.getTracks().forEach((t) => t.stop());
      remoteStreamRef.current = null;
    }
    callUnsubsRef.current.forEach((fn) => fn());
    callUnsubsRef.current = [];
    if (callDocIdRef.current) {
      updateDoc(doc(db, "calls", callDocIdRef.current), { status: "ended" }).catch(() => {});
      callDocIdRef.current = null;
    }
    setCallState(null);
    setCallType(null);
    setMicMuted(false);
    setCamOff(false);
    setIncomingCallData(null);
  };

  const declineCall = () => {
    stopRingtone();
    if (incomingCallData?.callId) {
      updateDoc(doc(db, "calls", incomingCallData.callId), { status: "declined" }).catch(() => {});
    }
    setCallState(null);
    setCallType(null);
    setIncomingCallData(null);
  };

  // ── startCall ─────────────────────────────────────────────────────────
  // type: "audio" | "video"
  // calledId: Firestore UID of the callee
  // calledUser: { displayName, photoURL } for the overlay
  const startCall = async (type, calledId, calledUser) => {
    if (!authUserId) return;
    try {
      setCallType(type);
      setCallState("calling");
      setOtherUserInfo(calledUser || { displayName: "Usuario", photoURL: "" });
      const stream = await navigator.mediaDevices.getUserMedia({
        video: type === "video",
        audio: {
          echoCancellation: true,
          noiseSuppression: false,
          autoGainControl: false,
          sampleRate: 48000,
          channelCount: 1,
        },
      });
      localStreamRef.current = stream;
      requestAnimationFrame(() => {
        if (localVideoRef.current) localVideoRef.current.srcObject = stream;
      });
      const pc = new RTCPeerConnection(ICE_SERVERS);
      peerConnectionRef.current = pc;
      stream.getTracks().forEach((track) => pc.addTrack(track, stream));
      const remoteStream = new MediaStream();
      remoteStreamRef.current = remoteStream;
      pc.ontrack = (event) => {
        event.streams[0].getTracks().forEach((track) => remoteStream.addTrack(track));
        requestAnimationFrame(() => {
          if (remoteVideoRef.current) remoteVideoRef.current.srcObject = remoteStream;
          if (remoteAudioRef.current) remoteAudioRef.current.srcObject = remoteStream;
        });
      };
      const callDocRef = doc(collection(db, "calls"));
      callDocIdRef.current = callDocRef.id;
      pc.onicecandidate = (event) => {
        if (event.candidate)
          addDoc(collection(db, "calls", callDocRef.id, "callerCandidates"), event.candidate.toJSON()).catch(() => {});
      };
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      await setDoc(callDocRef, {
        callerId: authUserId,
        calleeId: calledId,
        type,
        status: "ringing",
        callerName: authUserName,
        callerPhotoURL: authUserImage,
        offer: { type: offer.type, sdp: offer.sdp },
      });
      const unsubCall = onSnapshot(callDocRef, (snap) => {
        const data = snap.data();
        if (!data) return;
        if (data.answer && peerConnectionRef.current && !peerConnectionRef.current.remoteDescription) {
          peerConnectionRef.current
            .setRemoteDescription(new RTCSessionDescription(data.answer))
            .then(() => setCallState("active"))
            .catch(() => {});
        }
        if (data.status === "ended" || data.status === "declined") endCall();
      });
      const unsubCandidates = onSnapshot(collection(db, "calls", callDocRef.id, "calleeCandidates"), (snap) => {
        snap.docChanges().forEach((change) => {
          if (change.type === "added" && peerConnectionRef.current)
            peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(change.doc.data())).catch(() => {});
        });
      });
      callUnsubsRef.current = [unsubCall, unsubCandidates];
    } catch (err) {
      console.error("Error al iniciar llamada:", err);
      setCallState(null);
      setCallType(null);
    }
  };

  // ── answerCall ────────────────────────────────────────────────────────
  const answerCall = async () => {
    if (!incomingCallData) return;
    try {
      const { callId, type } = incomingCallData;
      setCallState("active");
      setIncomingCallData(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: type === "video",
        audio: {
          echoCancellation: true,
          noiseSuppression: false,
          autoGainControl: false,
          sampleRate: 48000,
          channelCount: 1,
        },
      });
      localStreamRef.current = stream;
      requestAnimationFrame(() => {
        if (localVideoRef.current) localVideoRef.current.srcObject = stream;
      });
      const pc = new RTCPeerConnection(ICE_SERVERS);
      peerConnectionRef.current = pc;
      stream.getTracks().forEach((track) => pc.addTrack(track, stream));
      const remoteStream = new MediaStream();
      remoteStreamRef.current = remoteStream;
      pc.ontrack = (event) => {
        event.streams[0].getTracks().forEach((track) => remoteStream.addTrack(track));
        requestAnimationFrame(() => {
          if (remoteVideoRef.current) remoteVideoRef.current.srcObject = remoteStream;
          if (remoteAudioRef.current) remoteAudioRef.current.srcObject = remoteStream;
        });
      };
      const callDocRef = doc(db, "calls", callId);
      callDocIdRef.current = callId;
      const callSnap = await getDoc(callDocRef);
      await pc.setRemoteDescription(new RTCSessionDescription(callSnap.data().offer));
      pc.onicecandidate = (event) => {
        if (event.candidate)
          addDoc(collection(db, "calls", callId, "calleeCandidates"), event.candidate.toJSON()).catch(() => {});
      };
      const callerCandidatesSnap = await getDocs(collection(db, "calls", callId, "callerCandidates"));
      for (const c of callerCandidatesSnap.docs)
        await pc.addIceCandidate(new RTCIceCandidate(c.data())).catch(() => {});
      const unsubCandidates = onSnapshot(collection(db, "calls", callId, "callerCandidates"), (snap) => {
        snap.docChanges().forEach((change) => {
          if (change.type === "added" && peerConnectionRef.current)
            peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(change.doc.data())).catch(() => {});
        });
      });
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      await updateDoc(callDocRef, { answer: { type: answer.type, sdp: answer.sdp }, status: "active" });
      const unsubCall = onSnapshot(callDocRef, (snap) => {
        if (snap.data()?.status === "ended") endCall();
      });
      callUnsubsRef.current = [unsubCall, unsubCandidates];
    } catch (err) {
      console.error("Error al contestar llamada:", err);
      endCall();
    }
  };

  const toggleMic = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getAudioTracks().forEach((t) => { t.enabled = !t.enabled; });
      setMicMuted((v) => !v);
    }
  };

  const toggleCam = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getVideoTracks().forEach((t) => { t.enabled = !t.enabled; });
      setCamOff((v) => !v);
    }
  };

  return (
    <CallContext.Provider value={{ startCall, endCall, callState, callType }}>
      {children}

      {/* ── Global call overlay ── rendered on top of any screen ── */}
      {callState && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 3000,
            background: "linear-gradient(160deg, #1a1a2e 0%, #0f3460 100%)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "space-between",
            overflow: "hidden",
          }}
        >
          {/* Remote video — always mounted inside overlay to avoid ref-null race */}
          <video
            ref={remoteVideoRef}
            autoPlay
            playsInline
            style={{
              position: "absolute", inset: 0, width: "100%", height: "100%",
              objectFit: "cover", zIndex: 0,
              display: callType === "video" && callState === "active" ? "block" : "none",
            }}
          />
          {/* Hidden audio element — handles remote audio for both audio and video calls */}
          <audio ref={remoteAudioRef} autoPlay playsInline style={{ display: "none" }} />

          <Box sx={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", alignItems: "center", pt: 10, gap: 1.5, width: "100%" }}>
            <Avatar
              src={otherUserInfo.photoURL || ""}
              sx={{
                width: 90,
                height: 90,
                border: "3px solid rgba(255,255,255,0.35)",
                boxShadow: callState === "incoming"
                  ? "0 0 0 10px rgba(255,255,255,0.08), 0 0 0 20px rgba(255,255,255,0.04)"
                  : "0 4px 20px rgba(0,0,0,0.4)",
              }}
            >
              {(otherUserInfo.displayName || "?")[0].toUpperCase()}
            </Avatar>
            <Typography variant="h6" color="white" fontWeight="700">
              {otherUserInfo.displayName}
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.65)" }}>
              {callState === "calling"
                ? "Llamando..."
                : callState === "incoming"
                ? callType === "video" ? "Videollamada entrante" : "Llamada entrante"
                : formatDuration(callDuration)}
            </Typography>
          </Box>

          {/* Local video — always mounted to avoid ref-null race */}
          <video
            ref={localVideoRef}
            autoPlay
            muted
            playsInline
            style={{
              position: "absolute", bottom: 110, right: 12, width: 100, height: 140,
              objectFit: "cover", borderRadius: 12, border: "2px solid rgba(255,255,255,0.4)", zIndex: 2,
              display: callType === "video" && callState === "active" ? "block" : "none",
            }}
          />

          <Box sx={{ position: "relative", zIndex: 3, display: "flex", gap: callState === "incoming" ? 8 : 4, pb: 7, alignItems: "center", justifyContent: "center", width: "100%" }}>
            {callState === "incoming" && (
              <>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.8 }}>
                  <IconButton onClick={declineCall} sx={{ bgcolor: "#e53935", color: "white", width: 66, height: 66, "&:hover": { bgcolor: "#c62828" } }}>
                    <CallEndIcon sx={{ fontSize: 32 }} />
                  </IconButton>
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)" }}>Rechazar</Typography>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.8 }}>
                  <IconButton onClick={answerCall} sx={{ bgcolor: "#43a047", color: "white", width: 66, height: 66, "&:hover": { bgcolor: "#2e7d32" } }}>
                    <CallIcon sx={{ fontSize: 32 }} />
                  </IconButton>
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)" }}>Contestar</Typography>
                </Box>
              </>
            )}
            {(callState === "calling" || callState === "active") && (
              <>
                {callState === "active" && (
                  <>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.8 }}>
                      <IconButton onClick={toggleMic} sx={{ bgcolor: micMuted ? "rgba(255,255,255,0.25)" : "rgba(255,255,255,0.12)", color: "white", width: 56, height: 56 }}>
                        {micMuted ? <MicOffIcon /> : <MicIcon />}
                      </IconButton>
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)" }}>{micMuted ? "Activar mic" : "Silenciar"}</Typography>
                    </Box>
                    {callType === "video" && (
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.8 }}>
                        <IconButton onClick={toggleCam} sx={{ bgcolor: camOff ? "rgba(255,255,255,0.25)" : "rgba(255,255,255,0.12)", color: "white", width: 56, height: 56 }}>
                          {camOff ? <VideocamOffIcon /> : <VideocamIcon />}
                        </IconButton>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)" }}>{camOff ? "Activar cámara" : "Cámara"}</Typography>
                      </Box>
                    )}
                  </>
                )}
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.8 }}>
                  <IconButton onClick={endCall} sx={{ bgcolor: "#e53935", color: "white", width: 66, height: 66, "&:hover": { bgcolor: "#c62828" } }}>
                    <CallEndIcon sx={{ fontSize: 32 }} />
                  </IconButton>
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)" }}>Finalizar</Typography>
                </Box>
              </>
            )}
          </Box>
        </Box>
      )}
    </CallContext.Provider>
  );
}
